import React, { useState, createContext, useContext } from 'react';
// Program görevinin yapısını tanımlayan interface
export interface ProgramGorevi {
  id: string;
  dersAdi: string;
  videoAdi: string;
  konuAdi: string; // Konu adı eklendi
  gun: string;
  saatAraligi: string;
  notlar?: string;
  tamamlandi: boolean; // Tamamlanma durumu eklendi (completed yerine tamamlandi olarak değiştirildi)
  etkinlikTipi?: 'study' | 'practice' | 'exam' | 'homework' | 'video' | 'resource' | 'test'; // Etkinlik tipi genişletildi
}
// Ders renkleri için sabitleri tanımlayalım
export const DERS_RENKLERI: Record<string, string> = {
  TÜRKÇE: 'from-red-600 to-pink-600',
  MATEMATİK: 'from-blue-500 to-cyan-500',
  'FEN BİLİMLERİ': 'from-emerald-600 to-teal-600',
  'T.C. İNKILAP TARİHİ VE ATATÜRKÇÜLÜK': 'from-amber-600 to-yellow-600',
  İNGİLİZCE: 'from-purple-500 to-violet-500',
  'DİN KÜLTÜRÜ VE AHLÂK BİLGİSİ': 'from-blue-900 to-blue-800',
  FİZİK: 'from-indigo-600 to-blue-700',
  KİMYA: 'from-green-600 to-emerald-600',
  BİYOLOJİ: 'from-teal-600 to-green-500',
  TARİH: 'from-amber-700 to-orange-600',
  COĞRAFYA: 'from-lime-600 to-green-600'
};
// Context'in yapısını tanımlayan interface
interface ProgramContextType {
  gorevler: ProgramGorevi[];
  gorevEkle: (gorev: Omit<ProgramGorevi, 'id'>) => void;
  toggleGorevDurumu: (id: string) => void;
  gorevSil: (id: string) => void; // Yeni fonksiyon eklendi
}
// Context'i oluştur
export const ProgramContext = createContext<ProgramContextType | undefined>(undefined);
// Custom hook oluştur - Context'e kolay erişim için
export const useProgramContext = () => {
  const context = useContext(ProgramContext);
  if (!context) {
    throw new Error('useProgramContext must be used within a ProgramProvider');
  }
  return context;
};
// Provider bileşeni
interface ProgramProviderProps {
  children: React.ReactNode;
}
export const ProgramProvider: React.FC<ProgramProviderProps> = ({
  children
}) => {
  const [gorevler, setGorevler] = useState<ProgramGorevi[]>(() => {
    // Local storage'dan görevleri yükle
    const savedGorevler = localStorage.getItem('programGorevleri');
    return savedGorevler ? JSON.parse(savedGorevler) : [];
  });
  // Yeni görev ekleme fonksiyonu
  const gorevEkle = (gorev: Omit<ProgramGorevi, 'id'>) => {
    // Generate a unique ID that includes a timestamp to prevent duplicates
    const uniqueId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    // Check if a similar task already exists to prevent duplicates
    const isDuplicate = gorevler.some(existingGorev => existingGorev.dersAdi === gorev.dersAdi && existingGorev.konuAdi === gorev.konuAdi && existingGorev.gun === gorev.gun && existingGorev.saatAraligi === gorev.saatAraligi);
    // Only add if it's not a duplicate
    if (!isDuplicate) {
      // FIX: Properly determine the activity type based on the source
      let activityType = gorev.etkinlikTipi;
      // If etkinlikTipi is not explicitly set, determine it based on context clues
      if (!activityType) {
        // Check if it's a homework
        if (gorev.konuAdi?.toLowerCase().includes('ödev') || gorev.videoAdi?.toLowerCase().includes('ödev') ||
        // Check for additional homework indicators
        gorev.notlar?.toLowerCase().includes('ödev') || gorev.notlar?.toLowerCase().includes('homework')) {
          activityType = 'homework';
        } else {
          // Default to study if no other indicators are present
          activityType = 'study';
        }
      }
      const yeniGorev: ProgramGorevi = {
        ...gorev,
        id: uniqueId,
        etkinlikTipi: activityType
      };
      const guncelGorevler = [...gorevler, yeniGorev];
      setGorevler(guncelGorevler);
      // Local storage'a kaydet
      localStorage.setItem('programGorevleri', JSON.stringify(guncelGorevler));
    }
  };
  // Görev durumunu değiştirme fonksiyonu
  const toggleGorevDurumu = (id: string) => {
    const guncelGorevler = gorevler.map(gorev => gorev.id === id ? {
      ...gorev,
      tamamlandi: !gorev.tamamlandi
    } : gorev);
    setGorevler(guncelGorevler);
    // Local storage'a kaydet
    localStorage.setItem('programGorevleri', JSON.stringify(guncelGorevler));
  };
  // Görev silme fonksiyonu
  const gorevSil = (id: string) => {
    const guncelGorevler = gorevler.filter(gorev => gorev.id !== id);
    setGorevler(guncelGorevler);
    // Local storage'a kaydet
    localStorage.setItem('programGorevleri', JSON.stringify(guncelGorevler));
  };
  return <ProgramContext.Provider value={{
    gorevler,
    gorevEkle,
    toggleGorevDurumu,
    gorevSil
  }} data-id="element-1392">
      {children}
    </ProgramContext.Provider>;
};